import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@cyphex/ui';
import type { UserId } from '@cyphex/types';
import { ROUTES } from '@/constants/routes';
import { chatApi } from '../api';
import { useMe } from './useMe';

/**
 * Open the DM with a person, creating it if it does not exist yet.
 *
 * A PERSON ID IS NOT A CONVERSATION ID. Both search surfaces used to navigate
 * straight to `/chat/<userId>`, which is a real route with an id that can never
 * resolve — the thread 404'd, the header fell back to "Chat / Offline / ?", and
 * sending produced `POST /v1/conversations/<userId>/messages -> 404`. The id
 * looked plausible enough (both are uuids) that the mistake stayed invisible
 * until something tried to use it.
 *
 * MESSAGING YOURSELF IS NOT A DM. The API dedupes the participant list, so
 * `{kind:'dm', memberIds:[me]}` collapses to one participant and is rejected
 * with "A DM has exactly two participants." Picking yourself out of search is a
 * perfectly ordinary thing to do — Teams and Slack both send you to your own
 * notes — so it routes to the self-chat instead of surfacing that error.
 *
 * Both creates are safe to repeat: `uq_conversations_member_hash` and the
 * self-chat's unique index make them idempotent, so "message Priya" returns the
 * same thread every time rather than stacking duplicates.
 */
export function useOpenDirectMessage(): (userId: UserId) => Promise<void> {
  const navigate = useNavigate();
  const me = useMe();

  return useCallback(
    async (userId: UserId) => {
      try {
        const conversation =
          userId === me?.id
            ? await chatApi.getSelfConversation()
            : await chatApi.createConversation({ kind: 'dm', memberIds: [userId] });
        navigate(ROUTES.chatThread(conversation.id));
      } catch {
        toast.error('Could not open that chat.');
      }
    },
    [me?.id, navigate],
  );
}
